import { zodResolver } from '@hookform/resolvers/zod'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Box,
  CardHeader,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CheckboxController from '@/components/inputs/CheckboxController'
import CustomButton from '@/components/inputs/CustomButton'
import { useInvalidateResourceQuery } from '@/features/resource/hooks/useResourceQuery'
import { useUpdateSteeringMultistepMutation } from '@/features/resource/hooks/useUpdateSteeringMultistepMutation'
import { getResourceSteeringMultistepSchema } from '@/features/resource/schemas'
import type { Resource, ResourceSteeringConfig, ResourceSteeringMultistepSchemaType } from '@/features/resource/types'

export type ResourceSteeringMultistepPanelProps = {
  resourceID: Resource['resourceID']
  steeringConfig: ResourceSteeringConfig | undefined
  onUpdateMultistepSettings: (data: ResourceSteeringMultistepSchemaType) => void
}

export type ResourceSteeringMultistepFormProps = {
  steeringConfig: ResourceSteeringConfig | undefined
  closeEditMode: () => void
  onSave: (data: ResourceSteeringMultistepSchemaType) => void
  isSaving: boolean
}

const SteeringMultistepTooltipContent = () => {
  const { t } = useTranslation()

  return (
    <div style={{ maxWidth: 300 }}>
      <Typography sx={{ color: 'white' }} variant="body2">
        {t('resources.steering.multistep.tooltip')}
      </Typography>
    </div>
  )
}

const SteeringMultistepForm = ({
  steeringConfig,
  closeEditMode,
  onSave,
  isSaving,
}: ResourceSteeringMultistepFormProps) => {
  const { t } = useTranslation()

  const form = useForm<ResourceSteeringMultistepSchemaType>({
    defaultValues: {
      isMultistepResource: steeringConfig?.isMultistepResource ?? false,
      multistepStrategy: steeringConfig?.multistepStrategy ?? null,
    },
    resolver: zodResolver(getResourceSteeringMultistepSchema()),
  })

  const { handleSubmit, watch, control } = form
  const isMultistepResource = watch('isMultistepResource')

  function onCancel() {
    closeEditMode()
  }

  const multistepStrategyOptions = [
    {
      id: 'EXCLUSIVE',
      value: 'EXCLUSIVE',
      label: t('resources.steering.multistep.multistep_strategy.value.exclusive'),
    },
    {
      id: 'ADDITIVE',
      value: 'ADDITIVE',
      label: t('resources.steering.multistep.multistep_strategy.value.additive'),
      disabled: true,
    },
  ]

  return (
    <FormProvider {...form}>
      <form aria-label={t('resources.steering.multistep.label')} onSubmit={handleSubmit(onSave)}>
        <Stack alignItems={'flex-start'} direction={'column'} sx={{ gap: 3 }}>
          <CheckboxController label={t('resources.steering.multistep.is_multistep')} name="isMultistepResource" />
          {isMultistepResource && (
            <Controller
              control={control}
              name="multistepStrategy"
              render={({ field }) => (
                <>
                  <FormLabel id="radiogroup-label-strategy">
                    {t('resources.steering.multistep.multistep_strategy.label')}
                  </FormLabel>
                  <RadioGroup aria-labelledby="radiogroup-label-strategy" {...field}>
                    {multistepStrategyOptions.map((option) => (
                      <FormControlLabel
                        key={option.id}
                        control={<Radio />}
                        disabled={option.disabled}
                        label={option.label}
                        value={option.value}
                      />
                    ))}
                  </RadioGroup>
                </>
              )}
            />
          )}
          <Box>
            <Stack direction={'row'} gap={2}>
              <CustomButton disabled={isSaving} type="submit" variant="contained">
                {t('common.button.save')}
              </CustomButton>
              <CustomButton variant="outlined" onClick={onCancel}>
                {t('common.button.cancel')}
              </CustomButton>
            </Stack>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  )
}

const SteeringMultistepPanel = ({
  resourceID,
  steeringConfig,
  onUpdateMultistepSettings,
}: ResourceSteeringMultistepPanelProps) => {
  const { t } = useTranslation()
  const [editMode, setEditMode] = useState<boolean>(false)
  const { updateSteeringMultistep } = useUpdateSteeringMultistepMutation(resourceID)
  const invalidateResource = useInvalidateResourceQuery()

  function onOpenEditMode() {
    setEditMode(true)
  }

  function onCloseEditMode() {
    setEditMode(false)
  }

  async function onSave(data: ResourceSteeringMultistepSchemaType) {
    try {
      await updateSteeringMultistep(data)
      onUpdateMultistepSettings(data)
      invalidateResource(resourceID)
      setEditMode(false)
    } catch (error) {
      console.error('Failed to update multistep resource settings:', error)
    }
  }

  function getMultistepString(steeringConfig?: ResourceSteeringConfig): string {
    return steeringConfig?.isMultistepResource
      ? t('resources.steering.multistep.is_multistep_description', { strategy: steeringConfig?.multistepStrategy })
      : t('resources.steering.multistep.is_not_multistep_description')
  }

  return (
    <>
      <CardHeader
        sx={{ paddingX: 0 }}
        title={
          <Stack alignItems="center" direction="row" spacing={1}>
            <CustomTypography variant="h6">{t('resources.steering.multistep.label')}</CustomTypography>
            <Tooltip arrow placement="right-start" title={<SteeringMultistepTooltipContent />}>
              <HelpOutlineIcon fontSize="small" sx={{ color: 'black' }} />
            </Tooltip>
          </Stack>
        }
      />
      {editMode && steeringConfig ? (
        <SteeringMultistepForm
          closeEditMode={onCloseEditMode}
          isSaving={false}
          steeringConfig={steeringConfig}
          onSave={onSave}
        />
      ) : (
        <Stack alignItems={'flex-start'} direction={'column'} sx={{ gap: 3, paddingBottom: 3 }}>
          <CustomTypography sx={{ paddingX: 3 }} variant={'body1'}>
            {getMultistepString(steeringConfig)}
          </CustomTypography>
          <CustomButton variant={'outlined'} onClick={onOpenEditMode}>
            {t('resources.steering.multistep.button')}
          </CustomButton>
        </Stack>
      )}
    </>
  )
}

export default SteeringMultistepPanel
